import { RefObject, useEffect } from 'react'

// Below this chip width the sticky-title translation is more annoying than
// helpful: the chip is already too narrow to fit any meaningful event-name
// text, so chasing the viewport just shuffles a "Cha..." ellipsis around.
// Chips narrower than this threshold fall back to the default react-calendar-
// timeline behavior (natural left-aligned title with ellipsis at the right).
const MIN_STICKY_ITEM_WIDTH_PX = 100
// How quickly each title offset catches up to the scroll position (0–1).
// react-calendar-timeline rounds scroll offset to whole pixels, so without
// smoothing the title snaps in visible steps inside the chip.
const STICKY_OFFSET_SMOOTHING = 0.45
const SCROLL_IDLE_MS = 120
const TRANSLATE_X_REGEX = /translate(?:X|3d)\(\s*(-?[\d.]+)/

const parseTranslateX = (transform: string): number => {
  const match = TRANSLATE_X_REGEX.exec(transform)
  if (!match) return 0
  const value = parseFloat(match[1])
  return Number.isFinite(value) ? value : 0
}

const parsePxValue = (value: string): number => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const resetContentStyles = (contentEl: HTMLElement) => {
  if (contentEl.style.transform !== '') {
    contentEl.style.transform = ''
  }
  if (contentEl.style.maxWidth !== '100%') {
    contentEl.style.maxWidth = '100%'
  }
}

const smoothOffset = (current: number, target: number): number => {
  if (current === target) return target
  const next = current + (target - current) * STICKY_OFFSET_SMOOTHING
  if (Math.abs(target - next) < 0.5) return target
  return next
}

const applyStickyOffset = (contentEl: HTMLElement, itemWidth: number, offset: number) => {
  const roundedOffset = Math.round(offset * 10) / 10
  const nextTransform = roundedOffset > 0 ? `translate3d(${roundedOffset}px, 0, 0)` : ''
  if (contentEl.style.transform !== nextTransform) {
    contentEl.style.transform = nextTransform
  }
  const nextMaxWidth = roundedOffset > 0 ? `${Math.max(0, itemWidth - roundedOffset)}px` : '100%'
  if (contentEl.style.maxWidth !== nextMaxWidth) {
    contentEl.style.maxWidth = nextMaxWidth
  }
}

const updateItemTitles = (canvasEl: HTMLElement, smoothedOffsets: WeakMap<HTMLElement, number>, snapToTarget: boolean) => {
  const canvasOffsetPx = -parseTranslateX(canvasEl.style.transform)
  const items = canvasEl.querySelectorAll<HTMLElement>('.rct-item')
  items.forEach((itemEl) => {
    const contentEl = itemEl.querySelector<HTMLElement>(':scope > .rct-item-content')
    if (!contentEl) return
    const itemWidth = parsePxValue(itemEl.style.width)

    // Skip narrow chips entirely: a chip that's already too small to display
    // any meaningful title text doesn't benefit from sticky-title translation.
    // Reset any styles a previous (wider) iteration may have left behind so
    // the chip falls back to the native ellipsis behavior cleanly.
    if (itemWidth < MIN_STICKY_ITEM_WIDTH_PX) {
      smoothedOffsets.delete(contentEl)
      resetContentStyles(contentEl)
      return
    }

    const itemLeft = parsePxValue(itemEl.style.left)
    const rawOffset = canvasOffsetPx - itemLeft
    // Pin the title to the visible (left) viewport edge while the item's start
    // has scrolled past it (`rawOffset > 0`). The offset is capped at the item
    // width — never at the title's natural width — so this works identically
    // for titles that fit and for long titles (e.g. Genshin Impact event
    // names) that don't: `applyStickyOffset` clips the title to
    // `itemWidth - offset` (the still-visible portion of the item), so the
    // title's right edge never crosses the item's right edge and the most
    // useful part of the title (its beginning) stays readable.
    //
    // Capping at `itemWidth` (rather than `itemWidth - tail`) also lets the
    // title collapse to zero width once the item is fully scrolled off,
    // instead of leaving a stuck "…" sliver pinned to the viewport edge.
    const targetOffset = Math.max(0, Math.min(rawOffset, itemWidth))
    const previousOffset = smoothedOffsets.get(contentEl) ?? targetOffset
    const offset = snapToTarget ? targetOffset : smoothOffset(previousOffset, targetOffset)
    smoothedOffsets.set(contentEl, offset)
    applyStickyOffset(contentEl, itemWidth, offset)
  })
}

/**
 * Keeps the title of each calendar item visible near the left viewport edge
 * when the item starts before the visible time range. Mirrors the sticky
 * behavior of native calendar event tiles (e.g. Google Calendar).
 *
 * `react-calendar-timeline` implements horizontal scrolling by translating
 * the canvas via `transform: translateX(...)`, and item components do not
 * re-render on scroll. We observe the canvas transform and run a short
 * requestAnimationFrame loop while the user is scrolling so title offsets
 * update every frame instead of jumping in coarse steps.
 */
export const useStickyCalendarItemTitles = (wrapperRef: RefObject<HTMLElement>): void => {
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || typeof MutationObserver === 'undefined') return

    let scrollEl: HTMLElement | null = null
    let canvasEl: HTMLElement | null = null
    let canvasObserver: MutationObserver | null = null
    let itemObserver: MutationObserver | null = null
    let resizeObserver: ResizeObserver | null = null
    let rafLoopId: number | null = null
    let scrollIdleTimeout: ReturnType<typeof setTimeout> | null = null
    let scrollActivityTarget: HTMLElement | null = null
    const smoothedOffsets = new WeakMap<HTMLElement, number>()

    const onScrollActivity = () => markScrollActivity()

    const cancelRafLoop = () => {
      if (rafLoopId !== null) {
        cancelAnimationFrame(rafLoopId)
        rafLoopId = null
      }
    }

    const runFrame = (snapToTarget: boolean) => {
      if (canvasEl) updateItemTitles(canvasEl, smoothedOffsets, snapToTarget)
    }

    const tick = () => {
      runFrame(false)
      rafLoopId = requestAnimationFrame(tick)
    }

    const startRafLoop = () => {
      if (rafLoopId === null) {
        rafLoopId = requestAnimationFrame(tick)
      }
    }

    const markScrollActivity = () => {
      startRafLoop()
      if (scrollIdleTimeout !== null) {
        clearTimeout(scrollIdleTimeout)
      }
      scrollIdleTimeout = setTimeout(() => {
        scrollIdleTimeout = null
        cancelRafLoop()
        runFrame(true)
      }, SCROLL_IDLE_MS)
    }

    const detachInner = () => {
      canvasObserver?.disconnect()
      canvasObserver = null
      itemObserver?.disconnect()
      itemObserver = null
      resizeObserver?.disconnect()
      resizeObserver = null
      cancelRafLoop()
      if (scrollIdleTimeout !== null) {
        clearTimeout(scrollIdleTimeout)
        scrollIdleTimeout = null
      }
      if (scrollActivityTarget) {
        scrollActivityTarget.removeEventListener('wheel', onScrollActivity)
        scrollActivityTarget.removeEventListener('pointerdown', onScrollActivity)
        scrollActivityTarget.removeEventListener('pointermove', onScrollActivity)
        scrollActivityTarget.removeEventListener('pointerup', onScrollActivity)
        scrollActivityTarget.removeEventListener('pointercancel', onScrollActivity)
        scrollActivityTarget = null
      }
      scrollEl = null
      canvasEl = null
    }

    const attach = (): boolean => {
      const nextScrollEl = wrapper.querySelector<HTMLElement>('.rct-scroll')
      const nextCanvasEl = nextScrollEl?.firstElementChild as HTMLElement | null
      if (!nextScrollEl || !nextCanvasEl) return false
      if (nextScrollEl === scrollEl && nextCanvasEl === canvasEl) return true

      detachInner()
      scrollEl = nextScrollEl
      canvasEl = nextCanvasEl

      const update = (snapToTarget = false) => {
        runFrame(snapToTarget)
      }
      update(true)

      // Observe item *repositioning* (`style.left`/`width`) on each `.rct-item`
      // wrapper. Arrow-key navigation shifts the visible time range, and once
      // it reaches the buffer edge react-calendar-timeline recenters the
      // canvas: every item's `left` changes while the canvas transform snaps
      // back to ~center (i.e. effectively unchanged). The same happens when
      // async-loaded events re-layout existing items. Without observing item
      // style mutations the canvas-transform observer never fires in those
      // cases, so titles freeze at stale offsets and disappear once their
      // start scrolls out of view (GR-2907).
      //
      // We deliberately observe the wrappers — never the `.rct-item-content`
      // children that `applyStickyOffset` writes to — so the hook's own
      // per-frame style writes don't generate mutation records (no self churn,
      // no risk of keeping the scroll-activity rAF loop alive). A dedicated
      // observer is disconnected and rebuilt on every item add/remove so
      // detached wrappers are dropped promptly instead of accumulating as
      // observed targets over long, high-churn sessions.
      itemObserver = new MutationObserver(() => {
        update(false)
        markScrollActivity()
      })
      const refreshItemObserver = () => {
        if (!canvasEl || !itemObserver) return
        itemObserver.disconnect()
        canvasEl.querySelectorAll<HTMLElement>('.rct-item').forEach((itemEl) => {
          itemObserver?.observe(itemEl, { attributes: true, attributeFilter: ['style'] })
        })
      }

      canvasObserver = new MutationObserver((mutations) => {
        // Rebuild the item observer when items are added/removed so freshly
        // mounted items report their future repositions (and detached ones
        // stop being observed).
        if (mutations.some((mutation) => mutation.type === 'childList')) {
          refreshItemObserver()
        }
        update(false)
        markScrollActivity()
      })
      canvasObserver.observe(canvasEl, {
        attributes: true,
        attributeFilter: ['style'],
      })
      const itemsContainer = canvasEl.querySelector('.rct-items')
      if (itemsContainer) {
        canvasObserver.observe(itemsContainer, { childList: true })
      }
      refreshItemObserver()

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => update(true))
        resizeObserver.observe(scrollEl)
      }

      scrollActivityTarget = scrollEl
      scrollEl.addEventListener('wheel', onScrollActivity, { passive: true })
      scrollEl.addEventListener('pointerdown', onScrollActivity, { passive: true })
      scrollEl.addEventListener('pointermove', onScrollActivity, { passive: true })
      scrollEl.addEventListener('pointerup', onScrollActivity, { passive: true })
      scrollEl.addEventListener('pointercancel', onScrollActivity, { passive: true })

      return true
    }

    attach()

    // Watch for the calendar DOM being created (initial mount) or replaced
    // (tab switch, re-mount). `childList` mutations fire when elements are
    // added/removed; transform changes are attribute mutations and are
    // intentionally not observed here to avoid double work.
    const wrapperObserver = new MutationObserver(() => {
      if (!scrollEl || !canvasEl || !wrapper.contains(scrollEl)) {
        attach()
      }
    })
    wrapperObserver.observe(wrapper, { childList: true, subtree: true })

    return () => {
      wrapperObserver.disconnect()
      detachInner()
    }
  }, [wrapperRef])
}
